import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Loader2, QrCode, RefreshCw, ShieldCheck, Zap } from 'lucide-react';

// WhatsApp 渠道綁定（2026-10-20 Kare 接入 AIBot pipeline）
// BYO 掃碼流：①開通（ensure，冪等）②顯示 QR——用 85261598890 嘅手機 WhatsApp「已連結的裝置」掃描
// ③狀態輪詢（只讀不燒 QR ref）→ connected 即綁定完成（AIBot 側 adopt 由營運收編入庫）
const AdminWaBind: React.FC = () => {
  const navigate = useNavigate();
  const [ensuring, setEnsuring] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qr, setQr] = useState<string | null>(null); // data:image/png;base64
  const [connected, setConnected] = useState<boolean | null>(null); // null=未知
  const [webhookMatch, setWebhookMatch] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) navigate('/admin');
  }, [navigate]);

  const pollStatus = useCallback(async () => {
    try {
      const r = await (await apiFetch('/api/admin/wa-bind/status')).json();
      const s = r?.data?.status ?? {};
      const isConnected = s.connected === true || s.status === 'connected';
      setConnected(isConnected);
      setWebhookMatch(s.webhook_match === true ? true : s.webhook_match === false ? false : null);
    } catch { /* 輪詢失敗靜默——下一輪再試 */ }
  }, []);

  useEffect(() => {
    pollStatus();
    timer.current = window.setInterval(pollStatus, 5000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [pollStatus]);

  const onEnsure = async () => {
    setEnsuring(true); setError(null); setNote(null);
    try {
      const r = await (await apiFetch('/api/admin/wa-bind/ensure', { method: 'POST' })).json();
      if (!r?.success) throw new Error(r?.error || '開通失敗');
      setNote('渠道開通請求已送出（冪等——重複按唔會重開）');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setEnsuring(false); }
  };

  const onQr = async () => {
    setQrLoading(true); setError(null); setNote(null);
    try {
      const r = await (await apiFetch('/api/admin/wa-bind/qr', { method: 'POST' })).json();
      const data = r?.data?.qr;
      const img = typeof data === 'object' && data ? (data.qr ?? data.data) : data;
      if (!r?.success || typeof img !== 'string' || !img.startsWith('data:image')) throw new Error(r?.error || 'QR 取得失敗（先按「開通渠道」）');
      setQr(img);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setQrLoading(false); }
  };

  return (
    <AdminLayout currentPage="wa-bind" onLogout={() => { localStorage.removeItem('admin_token'); navigate('/admin'); }}>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">WhatsApp 渠道綁定</h1>
          <p className="text-sm text-slate-500 mt-1">
            Kare 專用發送線（85261598890）· 經 AIBot 管道接入 · 客人回覆直接入此號手機由人手處理
          </p>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        {note && <div className="bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3 text-sm">{note}</div>}

        {connected === true ? (
          <div className="bg-green-50 border border-green-300 rounded-xl p-6 text-center space-y-2">
            <ShieldCheck className="w-12 h-12 text-green-600 mx-auto" />
            <div className="text-lg font-semibold text-green-800">已連線 ✓</div>
            <div className="text-sm text-green-700">
              渠道 85261598890 運作中{webhookMatch === true ? '（webhook 已掛）' : webhookMatch === false ? '——注意：webhook 未對上' : ''}
            </div>
            <div className="text-xs text-green-600">訂單／付款通知將由此號自動發送</div>
          </div>
        ) : (
          <>
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2 text-slate-700 font-medium"><Zap className="w-4 h-4" />步驟 ① 開通渠道（只需一次）</div>
              <p className="text-sm text-slate-500">系統會為 85261598890 建立渠道並掛收訊 webhook（冪等，重複按安全）。</p>
              <button onClick={onEnsure} disabled={ensuring}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {ensuring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                開通渠道
              </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
              <div className="flex items-center gap-2 text-slate-700 font-medium"><QrCode className="w-4 h-4" />步驟 ② 掃碼連結</div>
              <ol className="text-sm text-slate-600 list-decimal list-inside space-y-1">
                <li>先在<b>本頁所在的電腦／另一部手機</b>取得 QR（QR 不能用要掃碼的那部手機顯示）</li>
                <li>拿出 <b>85261598890</b> 的手機，開 WhatsApp → 設定 → <b>已連結的裝置</b> → <b>連結裝置</b></li>
                <li>用相機掃描下方 QR</li>
                <li>此頁會自動偵測連線（約 5 秒內變綠）</li>
              </ol>
              <div className="flex items-center gap-3">
                <button onClick={onQr} disabled={qrLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                  {qrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}
                  {qr ? '重新取得 QR' : '取得 QR'}
                </button>
                {qr && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" />QR 有時效，過期就重新取一次</span>}
              </div>
              {qr && (
                <div className="flex justify-center p-4 bg-white rounded-lg border">
                  <img src={qr} alt="WhatsApp 綁定 QR" className="w-64 h-64 object-contain" />
                </div>
              )}
              {connected === false && <div className="text-xs text-amber-600">目前狀態：未連線——掃碼後此處約 5 秒內自動更新</div>}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminWaBind;
